import type { PublicBranding } from '@/api/types/generated'
import { BrandTheme } from '@/shared/components/BrandTheme'
import { brandRamp, primaryTokensOnly } from '@/shared/utils/brandRamp'
import { useLocation, useSearchParams } from '@solidjs/router'
import {
  type Accessor,
  type JSX,
  createContext,
  createMemo,
  createResource,
  useContext,
} from 'solid-js'
import { clientPortalApi } from '../../../api/clientPortal.api'

type PortalBrandValue = {
  branding: Accessor<PublicBranding | null>
  companyName: Accessor<string | null>
  logoUrl: Accessor<string | null>
  viewerEmail: Accessor<string | null>
}

const PortalBrandContext = createContext<PortalBrandValue>()

const ACHROMATIC_PRIMARY_REMAP: JSX.CSSProperties = {
  '--color-primary': 'var(--brand)',
  '--color-primary-600': 'var(--brand)',
  '--color-primary-700': 'var(--brand-active)',
  '--color-primary-faint': 'var(--brand-soft)',
  '--color-on-primary': 'var(--on-brand)',
}

export function usePortalBrand(): PortalBrandValue {
  const ctx = useContext(PortalBrandContext)
  if (!ctx) {
    throw new Error('usePortalBrand must be used within PortalBrandProvider')
  }
  return ctx
}

export function PortalBrandProvider(props: { children: JSX.Element }) {
  const [params] = useSearchParams()
  const location = useLocation()
  const [data] = createResource(
    () => ({ w: params.w as string | undefined, token: params.token as string | undefined }),
    (q) => clientPortalApi.getBranding(q).catch(() => null)
  )

  const [viewer] = createResource(
    () => location.pathname,
    () => clientPortalApi.me().catch(() => null)
  )

  const branding = createMemo<PublicBranding | null>(() => data() ?? null)
  const companyName = createMemo(() => branding()?.company_name ?? null)
  const logoUrl = createMemo(() => branding()?.logo_url ?? null)
  const viewerEmail = createMemo<string | null>(() => viewer.latest?.email ?? null)

  const primaryRemap = createMemo<JSX.CSSProperties>(() => {
    const brandHex = branding()?.palette?.brand
    if (!brandHex) {
      return {}
    }
    const ramp = brandRamp(brandHex.toLowerCase())
    if (!ramp) {
      return ACHROMATIC_PRIMARY_REMAP
    }
    const rung500 = ramp.light['--color-primary-500']
    if (!rung500) {
      return {}
    }
    return { ...primaryTokensOnly(ramp.light), '--color-primary': rung500 }
  })

  const value: PortalBrandValue = { branding, companyName, logoUrl, viewerEmail }

  return (
    <PortalBrandContext.Provider value={value}>
      <BrandTheme branding={branding()} headingFontOnly>
        <div class="contents" style={primaryRemap()}>
          {props.children}
        </div>
      </BrandTheme>
    </PortalBrandContext.Provider>
  )
}
